import React, { useMemo } from 'react'

import { useSelector } from 'react-redux'
import moment from 'moment'
import { Tooltip } from 'antd'
import { snakeCase } from 'lodash'

import { EUROPEAN_DATE } from '../../../../utils/momentjs'
import { OverlaySettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { LaneTypes } from '../../../../Components/shared/Quotas/utils'
import { pc } from '../../../../utils/cssHelper'

const SPAN_STYLE = { justifyContent: 'center', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }
const SLOT_LABEL_STYLE = { position: 'absolute', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.9em', lineHeight: 1.3, padding: '1px 4px' }

const SchoolQuotaDisplayer = ({ quota, isDesiderata = false, labelSlot, overlay = new OverlaySettings() }) => {
  const t = useSelector(selectTranslate)

  const laneType = useMemo(() => quota[LaneTypes.SECTION] ? LaneTypes.SECTION : LaneTypes.GROUP, [quota[LaneTypes.SECTION]])

  const inline = labelSlot !== undefined || quota.total < (3 + !!quota[laneType])

  const labels = useMemo(() => {
    let institution = ''
    let school = ''
    let sector = ''

    if (isDesiderata) {
      institution = quota.sector?.institution ? quota.sector.institution.acronym || quota.sector.institution.name : ''
      sector = quota?.sector ? quota.sector?.acronym || quota.sector?.name : ''
    } else {
      school = quota.school.abbreviation || quota.school.name
    }

    const sectionOrGroup = quota[laneType]?.abbreviation ?? quota[laneType]?.name ?? ''
    const sectionToYear = quota?.sectionToYear ? ' (' + quota.sectionToYear.schoolYearName + ')' : ''

    return { institution, sector, school, sectionOrGroup, sectionToYear }
  }, [quota, isDesiderata])

  const tooltipTitle = useMemo(() => (
    <div>
      {t('school_quota_displayer.number_of_places.dd') + ' ' + quota.total} <br />
      {t('common.start_date.dd') + ' ' + moment(quota.startDate).format(EUROPEAN_DATE)} <br />
      {t('common.end_date.dd') + ' ' + moment(quota.endDate).format(EUROPEAN_DATE)} <br />
      {isDesiderata ? (
        <>
          {t('common.institution.dd')} {labels.institution} <br />
          {t('common.sector.dd')} {labels.sector} <br />
        </>
      ) : (
        <>
          {t('common.school.dd')} {labels.school} <br />
        </>
      )}
      {quota[laneType] && (t(`common.${snakeCase(laneType)}.dd`) + ' ' + labels.sectionOrGroup + labels.sectionToYear)}
    </div>
  ), [quota, labels, isDesiderata])

  const contentStyle = useMemo(() => {
    const style = {
      content: { ...SPAN_STYLE, overflowY: 'hidden', overflowX: 'hidden', textOverflow: 'ellipsis' },
      quantity: { ...SPAN_STYLE, alignContent: 'center' }
    }

    if (!labels.sectionOrGroup) {
      style.quantity.justifyContent = 'center'
    }

    if (labelSlot) {
      style.inline = { ...SLOT_LABEL_STYLE, top: pc(labelSlot.top), left: pc(labelSlot.left), width: pc(labelSlot.width) }
    } else {
      style.inline = style.content
    }

    return style
  }, [labels.sectionOrGroup, labelSlot])

  const quantityLabel = overlay.displayContentSize && <> {`${inline ? ' ' : ''}(${quota.total})`} </>

  const shouldDisplayBody = !isDesiderata || labels.sectionOrGroup

  return (
    <Tooltip title={tooltipTitle} className='w-100 h-100'>
      <div className='school-quota-displayer flex-row w-100 h-100 jc-ctr'>
        {shouldDisplayBody ? (
          <>
            {inline ? (
              <div className='flex-row w-100 h-100 jc-ctr ai-ctr'>
                {labelSlot !== null && (
                  <span style={contentStyle.inline}>
                    {overlay.displayContent && (
                      <>
                        {!isDesiderata && (<span style={contentStyle.content}><b>{labels.school}</b></span>)}
                        {(labels.sectionOrGroup ? (!isDesiderata ? ' — ' : '') + labels.sectionOrGroup : labels.sectionOrGroup) + labels.sectionToYear}
                      </>
                    )}
                    {quantityLabel}
                  </span>
                )}
              </div>
            ) : (
              <div className='flex-column w-100 h-100 jc-ctr'>
                {overlay.displayContent && (
                  <>
                    {!isDesiderata && (<span style={contentStyle.content}><b>{labels.school}</b></span>)}
                    {labels.sectionOrGroup && (<span style={contentStyle.content}>{labels.sectionOrGroup + labels.sectionToYear}</span>)}
                  </>
                )}
                <span style={contentStyle.quantity}>{quantityLabel}</span>
              </div>
            )}
          </>
        ) : (
          overlay.displayContentSize && <span style={contentStyle.quantity}>{quantityLabel}</span>
        )}
      </div>
    </Tooltip>
  )
}

export default SchoolQuotaDisplayer
